import {
	type ChangeEvent,
	type JSX,
	type ReactNode,
	type SelectHTMLAttributes,
} from 'react';
import { useFormContext } from 'react-hook-form';

import withForwardedRef from 'pkg/withForwardedRef';

import MaterialSymbol, { IconSizeMap } from 'components/material-symbols';

import type { InputChangeEvent } from 'components/form/inputs';
import InputBase, { Prefix } from 'components/form/inputs/input_base';

import * as css from './styles.css';

interface Props extends Omit<
	SelectHTMLAttributes<HTMLSelectElement>,
	'onChange' | 'prefix'
> {
	name?: string;
	small?: boolean;
	value?: string | number;
	defaultValue?: string | number;
	testid?: string;
	readonly?: boolean;
	prefix?: ReactNode;
	forwardedRef?: any;
	onChange?: InputChangeEvent;
	validate?: (
		value: string | number
	) => string | string[] | boolean | undefined;
}

/**
 * Select component
 *
 * A styled select dropdown that integrates with react-hook-form for validation.
 */
export const Select = withForwardedRef(
	({
		name,
		small,
		testid,
		disabled,
		className,
		readonly,
		prefix,
		forwardedRef,
		children,
		validate,
		onChange,
		...rest
	}: Props): JSX.Element => {
		const methods = useFormContext();

		const validationRules =
			name &&
			methods &&
			methods.register(name, {
				required: rest.required,
				validate,
			});

		let changeHandler: InputChangeEvent;
		if (onChange) {
			changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
				if (validationRules) {
					validationRules.onChange(event);
				}

				onChange(event);
			};
		} else if (!onChange && validationRules) {
			changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
				validationRules.onChange(event);
			};
		}

		let ref = forwardedRef;
		if (forwardedRef && validationRules) {
			ref = (e: HTMLInputElement) => {
				validationRules.ref(e);
				forwardedRef.current = e; // you can still assign to ref
			};
		} else if (validationRules) {
			ref = validationRules.ref;
		}

		return (
			<InputBase className={css.selectWrapper} small={small}>
				{prefix !== undefined && <Prefix grayFill>{prefix}</Prefix>}
				<select
					ref={ref}
					name={name}
					disabled={disabled || readonly}
					onChange={changeHandler}
					data-testid={testid}
					className={className}
					{...rest}>
					{children}
				</select>
				<div className={css.iconWrapper}>
					<MaterialSymbol variant="unfold_more" size={IconSizeMap.default} />
				</div>
			</InputBase>
		);
	}
);
